import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ElementRef,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { lastValueFrom } from "rxjs";
import { ModalDismissReasons, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SignatureSizeService } from "src/app/core/services/signature-size.service";
import Swal, { SweetAlertIcon } from "sweetalert2";

// Interface para SignatureArea
interface SignatureArea {
  id?: number;

  x: number;
  y: number;
  width: number;
  height: number;

  // NUEVO
  xPercent?: number;
  yPercent?: number;
  widthPercent?: number;
  heightPercent?: number;

  page: number;

  signer?: string;

  renderedPageWidth?: number;
  renderedPageHeight?: number;

  idLoteDetalleCoordenada?: number;
}

interface SignatureSize {
  id: number | string;
  nombre: string; // o 'descripcion' si tu API usa ese campo
  width: number;
  height: number;
}

@Component({
  selector: "app-file-viewer",
  templateUrl: "./file-viewer.component.html",
  styleUrls: ["./file-viewer.css"],
})
export class FileViewerComponent implements OnInit, OnDestroy, OnChanges {
  @Output() addCertificado = new EventEmitter<any>();
  @Output() deleteCertificado = new EventEmitter<any>();
  @Input() url_document: string | null = null;
  @Input() certificado: any;
  @Input() listCertificado: any;
  @Input() modulo: string = "firma";
  @Input() firma_esgrupo: false;
  @Input() usa_plantilla: false;
  @Input() read_only: false;

  // MODAL
  closeResult: string = "";

  // Ruta a tu archivo PDF
  pdfSrc = "/assets/documents/prueba_pdf.pdf";
  totalPages = 0;

  // Array para almacenar todas las áreas de firma seleccionadas
  selectedAreas: SignatureArea[] = [];
  // Objeto de área temporal mientras un usuario arrastra para seleccionar
  tempArea: Partial<SignatureArea> | null = null;
  // Referencia al elemento div de selección temporal en el DOM
  tempAreaDiv: HTMLElement | null = null;

  // Coordenadas de inicio para la selección
  startX = 0;
  startY = 0;

  // Dimensiones reales del PDF en puntos (72 DPI)
  public pdfRealWidth: number = 0;
  public pdfRealHeight: number = 0;
  public pageRotation: number = 0;

  //tamaño carta
  readonly PAGE_WIDTH_LETTER = 612;
  readonly PAGE_HEIGHT_LETTER = 792;

  // Propiedades para el modal de entrada del firmante
  showSignerModal = false;
  currentSignerName: string = "";
  idCertificado: number | string = "";
  firmanteSeleccionado: any;
  es_grupo: boolean;
  idGrupo: number;

  // Propiedades para el modal de entrada del firmante

  // Almacena el número de página para la firma que se está asignando
  signerInputPageNumber: number | null = null;

  // Almacena colores asignados a diferentes firmantes para una visualización consistente
  signerColors: { [signer: string]: string } = {};

  // Instancia de MutationObserver para detectar cambios en el DOM del visor de PDF
  private mutationObserver: MutationObserver;

  existFirmante: boolean = false;
  firmante_con_imagen: boolean = false;

  private isTouching: boolean = false;
  public isDiviceType: boolean = false;
  deviceType: "mobile" | "tablet" | "desktop" = "desktop";
  // Propiedad para controlar la visibilidad del menú
  showMenu: boolean = false;
  isEditMode: boolean = false;
  page_overlay: string = "page-overlay";
  @ViewChild("modalFileViewer") modalFileViewer: any;
  // Toast
  toast = Swal;

  // Activar o no el modo de áreas predefinidas
  usePredefinedAreas: boolean = true;

  // Lista de tamaños que vienen de la BD
  predefinedAreas: SignatureSize[] = [];

  // Id del tamaño seleccionado actualmente
  selectedTemplateId: number | string | null = null;

  // Para limitar cuántos botones rápidos se muestran en el toolbar
  maxQuickButtons = 3;

  // Para búsqueda dentro del dropdown
  searchSizeTerm: string = "";

  public zoom: number = 1;

  isLandscape: boolean = false;
  isPortrait: boolean = false;

  isScrolling = false;
  private scrollTimeout: any;

  private resizeHandler = () => {
    this.checkDeviceType();
  };

  get selectedTemplate(): SignatureSize | null {
    if (!this.predefinedAreas || this.predefinedAreas.length === 0) {
      return null;
    }

    const found = this.predefinedAreas.find(
      (t) => t.id === this.selectedTemplateId,
    );

    // Si hay uno seleccionado (y existe), ese
    if (found) return found;

    // Si aún no hay selección, usamos el primero como default
    return this.predefinedAreas[0];
  }

  get filteredPredefinedAreas(): SignatureSize[] {
    const term = (this.searchSizeTerm || "").toLowerCase().trim();
    if (!term) return this.predefinedAreas || [];

    return this.predefinedAreas.filter((tpl) =>
      tpl.nombre?.toLowerCase().includes(term),
    );
  }

  get selectedTemplateLabel(): string {
    const tpl = this.selectedTemplate;
    if (!tpl) return "Seleccionar tamaño";
    return tpl.nombre;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private modalService: NgbModal,
    private signatureSizeService: SignatureSizeService,
  ) {}

  /**
   * Hook del ciclo de vida: Inicializa el componente.
   * Carga las áreas guardadas y configura el MutationObserver.
   */
  ngOnInit() {
    this.getSignatureSizes();
    this.toastAlert();
    this.checkDeviceType(); // Verifica al iniciar
    this.readOnly();

    // Solo activa una vez el modo correspondiente
    if (this.isDiviceType && !this.isEditMode) {
      this.toggleMode();
    }

    // Listener resize correcto
    window.addEventListener("resize", this.resizeHandler);

    // Configura el MutationObserver para detectar cuando las páginas del PDF se renderizan/añaden al DOM
    const container = this.el.nativeElement.querySelector(
      ".pdf-scroll-container",
    );
    this.mutationObserver = new MutationObserver(() =>
      this.attachPageOverlay(),
    );
    // Observa los cambios en la lista de hijos y los cambios del subárbol dentro del contenedor de desplazamiento
    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Verificar si listCertificado ha cambiado
    if (changes["listCertificado"]) {
      this.cargarAreas();

      const listCertificadoChange = changes["listCertificado"];

      // Verifica si listCertificado ha cambiado
      if (listCertificadoChange) {
        // **Importante:** Añade esta comprobación:
        // Solo ejecuta si no es el primer valor (la inicialización)
        // O si el valor ha cambiado *realmente* (la referencia es distinta y no es la primera)
        if (
          !listCertificadoChange.firstChange &&
          listCertificadoChange.currentValue
        ) {
          this.cargarAreas();
        }
      }
    }
  }

  async getSignatureSizes() {
    const sizes = await lastValueFrom(this.signatureSizeService.getAll());

    this.predefinedAreas = (sizes || []) as SignatureSize[];

    if (!this.predefinedAreas.length) {
      this.selectedTemplateId = null;
      return;
    }

    // Si todavía no hay uno seleccionado, dejamos el primero por defecto
    if (this.selectedTemplateId == null) {
      this.selectedTemplateId = this.predefinedAreas[0].id;
    }
  }

  selectTemplate(tpl: SignatureSize) {
    if (!tpl) return;
    this.selectedTemplateId = tpl.id;
  }

  /**
   * Hook del ciclo de vida: Limpia cuando el componente se destruye.
   * Desconecta el MutationObserver para evitar fugas de memoria.
   */
  ngOnDestroy(): void {
    window.removeEventListener("resize", this.resizeHandler);

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Manejador de eventos para cuando el visor de PDF ha cargado el documento.
   * @param pdf El objeto proxy del documento PDF de la biblioteca pdf-viewer.
   */

  onPdfLoaded(pdf: any) {
    pdf.getPage(1).then((page: any) => {
      // Capturamos la rotación (0, 90, 180, 270)
      this.pageRotation = page.rotate;

      const viewport = page.getViewport({ scale: 1 });
      this.pdfRealWidth = viewport.width;
      this.pdfRealHeight = viewport.height;

      // Detectar orientación
      this.isLandscape = viewport.width > viewport.height;
      this.isPortrait = viewport.height >= viewport.width;
    });
  }

  /**
   * Adjunta una superposición transparente a cada página del PDF y configura los detectores de eventos para la selección.
   * Este método se activa por el MutationObserver cuando aparecen nuevas páginas en el DOM.
   */
  attachPageOverlay() {
    let cont = 0;

    const pages = this.el.nativeElement.querySelectorAll(".page");
    pages.forEach((page: HTMLElement) => {
      // Obtener el número de página inmediatamente.
      const pageNumber = parseInt(
        page.getAttribute("data-page-number") || "0",
        10,
      );

      // Comprueba si ya existe una superposición para esta página para evitar añadir duplicados
      if (page.querySelector("." + this.page_overlay)) {
        // La superposición ya existe. NO necesitamos volver a renderizar las áreas aquí en cada mutación.
        // Las áreas se volverán a renderizar a través de renderAllSignatureAreas cuando selectedAreas cambie.
        return; // Saltar esta página si la superposición ya está adjunta
      }

      // Si no existe ninguna superposición, créala y añádela.
      // Crea el div de superposición de página usando Renderer2 de Angular para la manipulación del DOM
      const overlay = this.renderer.createElement("div");
      this.renderer.addClass(overlay, this.page_overlay);
      this.renderer.setStyle(overlay, "position", "absolute");
      this.renderer.setStyle(overlay, "top", "0");
      this.renderer.setStyle(overlay, "left", "0");
      this.renderer.setStyle(overlay, "width", "100%");
      this.renderer.setStyle(overlay, "height", "100%");
      this.renderer.setStyle(overlay, "z-index", "8");

      // Adjunta detectores de eventos del ratón a la superposición para la selección
      // Adjunta detectores de eventos del ratón
      this.renderer.listen(overlay, "mousedown", (event: MouseEvent) =>
        this.startSelection(
          this.getCoordsFromEvent(event),
          event.target,
          pageNumber,
        ),
      );
      this.renderer.listen(overlay, "mousemove", (event: MouseEvent) =>
        this.updateSelection(this.getCoordsFromEvent(event)),
      );
      this.renderer.listen(overlay, "mouseup", () => this.endSelection());

      // Adjunta detectores de eventos táctiles
      this.renderer.listen(overlay, "touchstart", (event: TouchEvent) => {
        // Previene el zoom y el comportamiento de mouse del navegador
        event.preventDefault();
        this.startSelection(
          this.getCoordsFromEvent(event),
          event.target,
          pageNumber,
        );
      });
      this.renderer.listen(overlay, "touchmove", (event: TouchEvent) => {
        event.preventDefault(); // Previene el desplazamiento de la página
        this.updateSelection(this.getCoordsFromEvent(event));
      });
      this.renderer.listen(overlay, "touchend", () => {
        this.endSelection();
      });

      // Añade la superposición al elemento de la página actual
      this.renderer.appendChild(page, overlay);

      // IMPORTANTE: Después de añadir la superposición, renderiza *inicialmente*
      // cualquier área de firma existente que pertenezca a esta página.
      // Las actualizaciones posteriores serán gestionadas por renderAllSignatureAreas cuando selectedAreas cambie.
      this.renderAreasForPage(page, pageNumber);
    });
  }

  private getCoordsFromEvent(event: MouseEvent | TouchEvent) {
    if ("touches" in event && event.touches.length > 0) {
      const touch = event.touches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY,
      };
    }

    if ("changedTouches" in event && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY,
      };
    }

    return {
      clientX: (event as MouseEvent).clientX,
      clientY: (event as MouseEvent).clientY,
    };
  }

  /**
   * Inicia una nueva selección de área de firma.
   * @param event El evento del ratón.
   * @param pageNumber El número de página donde se inicia la selección.
   */

  startSelection(
    coords: { clientX: number; clientY: number },
    target: EventTarget,
    pageNumber: number,
  ) {
    // Solo botón izquierdo de mouse (si existe event como MouseEvent)
    if (event instanceof MouseEvent && event.button !== 0) return;

    this.isTouching = true;

    const pageElement = (target as HTMLElement).closest(".page") as HTMLElement;
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const renderedPageWidth = pageElement.offsetWidth;
    const renderedPageHeight = pageElement.offsetHeight;

    // Posición del puntero relativa a la página
    this.startX = coords.clientX - rect.left;
    this.startY = coords.clientY - rect.top;

    // Limpia cualquier div temporal anterior
    if (this.tempAreaDiv) {
      const parent = this.renderer.parentNode(this.tempAreaDiv);
      if (parent) {
        this.renderer.removeChild(parent, this.tempAreaDiv);
      }
    }

    // Crea el div temporal
    this.tempAreaDiv = this.renderer.createElement("div");
    this.renderer.addClass(this.tempAreaDiv, "select-area");
    this.renderer.addClass(this.tempAreaDiv, "temp");
    this.renderer.setStyle(this.tempAreaDiv, "position", "absolute");

    // 🔹 MODO ÁREA PREDEFINIDA (drag & drop de un tamaño fijo)
    // 🔹 MODO ÁREA PREDEFINIDA
    if (this.usePredefinedAreas && this.selectedTemplate) {
      const tpl = this.selectedTemplate;

      /**
       * ==========================================
       * 🔥 SCALE BASE RESPONSIVE
       * ==========================================
       */

      let responsiveScale = renderedPageWidth / this.pdfRealWidth / this.zoom;

      /**
       * ==========================================
       * 🔥 CONTROL VISUAL UX
       * ==========================================
       */

      // Desktop
      if (window.innerWidth > 1200) {
        responsiveScale = Math.min(responsiveScale, 0.7);
      }

      // Tablet
      else if (window.innerWidth > 768) {
        responsiveScale = Math.min(responsiveScale, 0.6);
      }

      // Mobile
      else {
        responsiveScale = Math.min(responsiveScale, 0.25);
      }

      // Evitar miniaturas
      responsiveScale = Math.max(responsiveScale, 0.18);

      /**
       * ==========================================
       * 🔥 TAMAÑO FINAL
       * ==========================================
       */

      const dynamicWidth = tpl.width * responsiveScale * this.zoom;
      const dynamicHeight = tpl.height * responsiveScale * this.zoom;

      /**
       * ==========================================
       * 🔥 CENTRAR
       * ==========================================
       */

      let x = this.startX - dynamicWidth / 2;

      let y = this.startY - dynamicHeight / 2;

      /**
       * ==========================================
       * 🔥 CLAMP
       * ==========================================
       */

      if (x < 0) {
        x = 0;
      }

      if (y < 0) {
        y = 0;
      }

      if (x + dynamicWidth > renderedPageWidth) {
        x = renderedPageWidth - dynamicWidth;
      }

      if (y + dynamicHeight > renderedPageHeight) {
        y = renderedPageHeight - dynamicHeight;
      }

      /**
       * ==========================================
       * 🔥 TEMP AREA
       * ==========================================
       */

      this.tempArea = {
        x: Math.round(x),
        y: Math.round(y),

        width: Math.round(dynamicWidth),
        height: Math.round(dynamicHeight),

        page: pageNumber,

        renderedPageWidth,
        renderedPageHeight,
      };

      /**
       * ==========================================
       * 🔥 PREVIEW
       * ==========================================
       */

      this.renderer.setStyle(this.tempAreaDiv, "top", `${this.tempArea.y}px`);

      this.renderer.setStyle(this.tempAreaDiv, "left", `${this.tempArea.x}px`);

      this.renderer.setStyle(
        this.tempAreaDiv,
        "width",
        `${this.tempArea.width}px`,
      );

      this.renderer.setStyle(
        this.tempAreaDiv,
        "height",
        `${this.tempArea.height}px`,
      );
    } else {
      // 🔹 MODO DIBUJO LIBRE (lo que ya tenías)
      this.tempArea = {
        x: this.startX,
        y: this.startY,
        width: 0,
        height: 0,
        page: pageNumber,
        renderedPageWidth,
        renderedPageHeight,
      };

      this.renderer.setStyle(this.tempAreaDiv, "top", `${this.startY}px`);
      this.renderer.setStyle(this.tempAreaDiv, "left", `${this.startX}px`);
      this.renderer.setStyle(this.tempAreaDiv, "width", "0px");
      this.renderer.setStyle(this.tempAreaDiv, "height", "0px");
    }

    this.renderer.appendChild(pageElement, this.tempAreaDiv);
  }

  // ARRASTRAR
  updateSelection(coords: { clientX: number; clientY: number }) {
    if (!this.tempArea || !this.tempAreaDiv || !this.isTouching) return;

    const pageElement = this.tempAreaDiv.closest(".page") as HTMLElement;
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const currentX = coords.clientX - rect.left;
    const currentY = coords.clientY - rect.top;

    // 🔹 Si estamos usando plantillas: mover rectángulo fijo
    if (this.usePredefinedAreas && this.selectedTemplate) {
      const tpl = this.selectedTemplate;

      // Centro del rect en el puntero
      let newX = currentX - this.tempArea.width! / 2;
      let newY = currentY - this.tempArea.height! / 2;

      // Clampear usando el ancho real del elemento de la página
      const maxWidth = pageElement.offsetWidth;
      const maxHeight = pageElement.offsetHeight;

      // Clampear para no salir de la página
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;
      if (newX + this.tempArea.width! > maxWidth) {
        newX = maxWidth - this.tempArea.width!;
      }
      if (newY + this.tempArea.height! > maxHeight) {
        newY = maxHeight - this.tempArea.height!;
      }

      this.tempArea.x = Math.round(newX);
      this.tempArea.y = Math.round(newY);

      this.renderer.setStyle(this.tempAreaDiv, "left", `${this.tempArea.x}px`);
      this.renderer.setStyle(this.tempAreaDiv, "top", `${this.tempArea.y}px`);
      // width/height no cambian
      return;
    }

    // 🔹 Si NO usamos plantillas: tu lógica de dibujo libre (tal cual la tenías)
    const width = Math.abs(currentX - this.startX);
    const height = Math.abs(currentY - this.startY);
    const newX = Math.min(this.startX, currentX);
    const newY = Math.min(this.startY, currentY);

    this.tempArea.width = Math.round(width);
    this.tempArea.height = Math.round(height);
    this.tempArea.x = Math.round(newX);
    this.tempArea.y = Math.round(newY);

    this.renderer.setStyle(this.tempAreaDiv, "width", `${Math.round(width)}px`);
    this.renderer.setStyle(
      this.tempAreaDiv,
      "height",
      `${Math.round(height)}px`,
    );
    this.renderer.setStyle(this.tempAreaDiv, "left", `${Math.round(newX)}px`);
    this.renderer.setStyle(this.tempAreaDiv, "top", `${Math.round(newY)}px`);
  }

  endSelection() {
    if (!this.isTouching) {
      return;
    }

    this.isTouching = false;

    if (this.tempAreaDiv) {
      // Elimina el div de selección temporal del DOM
      const parentElement = this.renderer.parentNode(this.tempAreaDiv);
      if (parentElement) {
        this.renderer.removeChild(parentElement, this.tempAreaDiv);
      }
      this.tempAreaDiv = null; // Limpia la referencia
    }

    // Comprueba si se seleccionó un área válida (con ancho y alto no nulos)
    if (this.tempArea?.width && this.tempArea?.height) {
      this.signerInputPageNumber = this.tempArea.page || null; // Almacena la página para el contexto en el modal
      this.showSignerModal = true; // Muestra el modal de firmante personalizado

      this.firmanteSeleccionado = "";

      if (this.modulo != "simple") {
        this.openModal(this.modalFileViewer);
      } else {
        this.agregarFirmaSimple();
      }
    } else {
      this.tempArea = null; // Limpia tempArea si no se realizó una selección válida
    }
  }

  firmante(crt: any) {
    // Si no selecciona nada
    if (!crt) {
      this.existFirmante = false;
      this.idCertificado = "";
      this.currentSignerName = "";
      this.es_grupo = false;
      this.firmante_con_imagen = false;
      return;
    }

    const idCert = crt.id_certificado ?? "";
    const idGrupo = crt.id_grupo ?? "";
    this.firmante_con_imagen = crt.firmar_con_imagen;

    if (this.firma_esgrupo) {
      const estoySeleccionandoGrupo = !!crt.es_grupo;

      // ¿Ya hay un grupo seleccionado en el array?
      // const yaHayGrupoEnSelectedAreas = this.selectedAreas.some(
      //   (x: any) => x.es_grupo && Number(x.id_grupo) === Number(idGrupo)
      // );

      // 👉 Caso problemático:
      // Estoy intentando seleccionar alguien que NO es grupo
      // y aún NO hay grupo seleccionado
      if (!estoySeleccionandoGrupo && this.selectedAreas.length === 0) {
        Swal.fire({
          title: "Seleccione primero el grupo",
          text: "Debe seleccionar el grupo primero para continuar con el proceso",
          icon: "warning",
          confirmButtonText: "Ok",
        });

        this.existFirmante = false;
        this.idCertificado = "";
        this.currentSignerName = "";
        this.es_grupo = false;
        return;
      }
    }

    if (idCert !== "") {
      const yaExiste = this.selectedAreas.some(
        (x: any) => String(x.id_certificado ?? x.id) === String(idCert),
      );

      if (yaExiste) {
        this.existFirmante = false;
        Swal.fire({
          title: "Firmante existente",
          text: "Ya existe este firmante para el proceso de firma",
          icon: "warning",
          confirmButtonText: "Ok",
        });
        // reset
        this.firmanteSeleccionado = null;
        this.idCertificado = "";
        this.currentSignerName = "";
        this.es_grupo = false;
        return;
      }

      // set datos del firmante
      this.existFirmante = true;
      this.idCertificado = idCert;
      this.currentSignerName = crt.name;
      this.es_grupo = !!crt.es_grupo;
      return;
    }

    if (idGrupo !== "") {
      const grupoExiste = this.selectedAreas.some(
        (x: any) => Number(x.id_grupo) === Number(idGrupo),
      );

      if (grupoExiste) {
        this.existFirmante = false;
        Swal.fire({
          title: "Grupo existente",
          text: "Ya existe este grupo en el proceso",
          icon: "warning",
          confirmButtonText: "Ok",
        });
        this.firmanteSeleccionado = null;
        this.currentSignerName = "";
        return;
      }

      // si necesitas setear datos de grupo:
      this.existFirmante = true;
      this.idGrupo = idGrupo;
      this.currentSignerName = crt.name ?? "";
      this.es_grupo = true;
      return;
    }

    // fallback
    this.existFirmante = false;
    this.currentSignerName = "";
  }

  /**
   * Confirma el nombre del firmante introducido en el modal y añade la nueva área de firma.
   */
  confirmSigner() {
    if (this.tempArea && this.currentSignerName.trim()) {
      // Asegúrate de que todas las propiedades necesarias estén disponibles para la conversión
      const x = this.tempArea.x || 0;
      const y = this.tempArea.y || 0;
      const width = this.tempArea.width || 0;
      const height = this.tempArea.height || 0;
      const page = this.tempArea.page || 0;
      // Usa las dimensiones renderizadas guardadas, con un fallback a las dimensiones LETTER si por alguna razón no existen.
      const renderedPageWidth =
        this.tempArea.renderedPageWidth || this.PAGE_WIDTH_LETTER;
      const renderedPageHeight =
        this.tempArea.renderedPageHeight || this.PAGE_HEIGHT_LETTER;

      // 🔥 NORMALIZAR
      const xPercent = x / renderedPageWidth;
      const yPercent = y / renderedPageHeight;
      const widthPercent = width / renderedPageWidth;
      const heightPercent = height / renderedPageHeight;

      this.selectedAreas.push({
        ...this.tempArea,

        xPercent,
        yPercent,
        widthPercent,
        heightPercent,

        id: this.idCertificado == "" ? this.genId() : this.idCertificado,
        signer: this.currentSignerName.trim(),
      } as SignatureArea);

      // Construye el objeto que se va a guardar, incluyendo la cadena de coordenadas convertida
      let areasToSave: any;

      if (this.modulo == "Plantilla") {
        areasToSave = {
          descripcion: this.currentSignerName.trim(),
          coordenada: this.convertToUanatacaFormat(
            x,
            y,
            width,
            height,
            renderedPageWidth,
            renderedPageHeight,
          ),
          coordenada_frontend:
            xPercent +
            "," +
            yPercent +
            "," +
            widthPercent +
            "," +
            heightPercent,
          pagina: page,
        };
      } else {
        areasToSave = {
          modelo: "",
          id_item: 0,
          id_certificado: this.idCertificado,
          es_grupo: this.es_grupo,
          id_grupo: this.idGrupo,
          coordenada: this.convertToUanatacaFormat(
            x,
            y,
            width,
            height,
            renderedPageWidth,
            renderedPageHeight,
          ),
          coordenada_frontend:
            xPercent +
            "," +
            yPercent +
            "," +
            widthPercent +
            "," +
            heightPercent,
          pagina: page,
        };
      }

      this.guardarAreas(areasToSave);
      this.renderAllSignatureAreas(); // Vuelve a renderizar todas las áreas para actualizar la interfaz de usuario
    }
    this.closeSignerModal(); // Cierra y reinicia el modal
  }

  /**
   * Cancela el proceso de entrada del firmante y descarta la selección temporal.
   */
  cancelSigner() {
    this.showSignerModal = false;
    this.idCertificado = "";
    this.currentSignerName = "";
    this.existFirmante = false;
    this.closeSignerModal(); // Cierra y reinicia el modal
  }

  /**
   * Reinicia las propiedades del modal del firmante y lo oculta.
   */
  private closeSignerModal() {
    this.currentSignerName = "";
    this.tempArea = null; // Descarta la selección si no se confirmó
    this.signerInputPageNumber = null;
    this.showSignerModal = false;
    this.modalService.dismissAll();
  }
  // --- Fin de los métodos del Modal de Firmante ---

  /**
   * Elimina un área de firma por su ID.
   * @param id El ID del área de firma a eliminar.
   */
  removeArea(id: number, area: any) {
    Swal.fire({
      title: "¿Estás seguro de eliminar esta firma?",
      showCancelButton: true,
      icon: "warning",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        this.selectedAreas = this.selectedAreas.filter((a) => a.id !== id);
        if (this.modulo == "Plantilla") {
          this.deleteCertificado.emit(id);
        } else {
          //idLoteDetalleCoordenada
          this.deleteCertificado.emit(area);
        }
        this.renderAllSignatureAreas();
      } else {
        Swal.fire("Proceso Cancelado", "", "info");
      }
    });
  }

  /**
   * Guarda las `selectedAreas` actuales en `localStorage`.
   * Solo se almacenan los datos esenciales (sin referencias a HTMLElement).
   */
  guardarAreas(areasToSave) {
    this.addCertificado.emit(areasToSave);
  }

  /**
   * Carga las `selectedAreas` desde `localStorage`.
   */
  cargarAreas() {
    this.selectedAreas = [];
    this.listCertificado.forEach((cert, index) => {
      const coordenadas = cert.coordenada_frontend || "";
      const [xPercent, yPercent, widthPercent, heightPercent] = coordenadas
        .split(",")
        .map(Number);

      const pageElement = this.el.nativeElement.querySelector(
        `.page[data-page-number="${cert.pagina}"]`,
      ) as HTMLElement;

      const renderedWidth = pageElement?.offsetWidth || this.PAGE_WIDTH_LETTER;
      const renderedHeight =
        pageElement?.offsetHeight || this.PAGE_HEIGHT_LETTER;

      const x = xPercent * renderedWidth;
      const y = yPercent * renderedHeight;
      const width = widthPercent * renderedWidth;
      const height = heightPercent * renderedHeight;

      let dataCordenadas: any;

      if (this.modulo === "Plantilla") {
        dataCordenadas = {
          id: cert.id,

          x,
          y,
          width,
          height,

          xPercent,
          yPercent,
          widthPercent,
          heightPercent,

          page: cert.pagina,
          signer: cert.descripcion,
          idLoteDetalleCoordenada: cert.id_plantilla,
        };
      } else {
        dataCordenadas = {
          id: cert.certificado ? cert.certificado.id : this.genId(),

          x,
          y,
          width,
          height,

          xPercent,
          yPercent,
          widthPercent,
          heightPercent,

          page: cert.pagina,

          signer: cert.certificado
            ? cert.certificado.usuario.firstName +
              " " +
              cert.certificado.usuario.lastName
            : cert.nombre_grupo,

          idLoteDetalleCoordenada: cert.id,

          id_grupo: cert.id_grupo ? cert.id_grupo : "",
        };
      }
      // Si ya existe un elemento en esa posición, lo reemplazamos
      if (this.selectedAreas[index]) {
        this.selectedAreas[index] = dataCordenadas;
      } else {
        this.selectedAreas.push(dataCordenadas);
      }
    });

    this.renderAllSignatureAreas();
  }

  /**
   * Genera un color pastel consistente para un nombre de firmante dado.
   * @param signer El nombre del firmante.
   * @returns Una cadena de color pastel (formato HSL).
   */
  getColorForSigner(signer: string): string {
    if (this.signerColors[signer]) {
      return this.signerColors[signer];
    }

    let hash = 0;

    for (let i = 0; i < signer.length; i++) {
      hash = signer.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);

    const pastelColor = `hsl(${hue}, 70%, 85%)`;

    this.signerColors[signer] = pastelColor;

    return pastelColor;
  }

  // --- Renderización de Áreas de Firma en Páginas ---

  /**
   * Renderiza todas las áreas de firma que pertenecen a un elemento de página específico.
   * Este método asegura que las áreas estén correctamente posicionadas dentro de sus respectivas páginas.
   * @param pageElement El HTMLElement de la página PDF.
   * @param pageNumber El número de la página.
   */
  private renderAreasForPage(pageElement: HTMLElement, pageNumber: number) {
    // Elimina cualquier área de firma previamente renderizada de esta página para evitar duplicados
    const existingAreas = pageElement.querySelectorAll(
      ".rendered-signature-area",
    );
    existingAreas.forEach((areaDiv) =>
      this.renderer.removeChild(pageElement, areaDiv),
    );

    // Filtra las selectedAreas para encontrar solo aquellas que pertenecen a la página actual
    const areasOnThisPage = this.selectedAreas.filter(
      (area) => area.page === pageNumber,
    );
    areasOnThisPage.forEach((area) => {
      const renderedWidth = pageElement.offsetWidth;
      const renderedHeight = pageElement.offsetHeight;

      // 🔥 RECALCULAR SEGÚN TAMAÑO ACTUAL
      const realX = area.xPercent * renderedWidth;
      const realY = area.yPercent * renderedHeight;

      const realWidth = area.widthPercent * renderedWidth;
      const realHeight = area.heightPercent * renderedHeight;
      const signerColor = this.getColorForSigner(area.signer || "");
      // Crea el div para el área de firma
      const areaDiv = this.renderer.createElement("div");
      this.renderer.addClass(areaDiv, "select-area");
      this.renderer.addClass(areaDiv, "rendered-signature-area"); // Añade una clase para identificarlos y eliminarlos fácilmente más tarde
      this.renderer.setStyle(areaDiv, "position", "absolute"); // Posicionado en relación con la página
      this.renderer.setStyle(areaDiv, "top", `${realY}px`);
      this.renderer.setStyle(areaDiv, "left", `${realX}px`);
      this.renderer.setStyle(areaDiv, "width", `${realWidth}px`);
      this.renderer.setStyle(areaDiv, "height", `${realHeight}px`);
      this.renderer.setStyle(
        areaDiv,
        "borderColor",
        this.getColorForSigner(area.signer || ""),
      );
      this.renderer.setStyle(
        areaDiv,
        "backgroundColor",
        this.getColorForSigner(area.signer || ""),
      ); // Rellena el fondo para retroalimentación visual
      this.renderer.setStyle(areaDiv, "opacity", "0.9");

      // Crea y añade el span de la etiqueta para el nombre del firmante
      const labelSpan = this.renderer.createElement("span");
      this.renderer.addClass(labelSpan, "area-label");
      this.renderer.setStyle(
        labelSpan,
        "backgroundColor",
        this.getColorForSigner(area.signer || ""),
      );
      this.renderer.setStyle(labelSpan, "color", "#000"); // Asegura que el texto sea negro para el contraste
      this.renderer.setProperty(labelSpan, "textContent", area.signer);
      this.renderer.appendChild(areaDiv, labelSpan);

      if (!this.read_only && !this.usa_plantilla) {
        // Crea y añade el botón de eliminar

        const removeButton = this.renderer.createElement("button");
        this.renderer.addClass(removeButton, "remove-btn");
        this.renderer.setProperty(removeButton, "textContent", "×"); // Muestra 'x'
        // Adjunta el detector de clics para el botón de eliminar
        this.renderer.listen(removeButton, "click", (event: MouseEvent) => {
          event.stopPropagation(); // Evita que el clic se propague al div de área padre
          this.removeArea(area.id, area);
        });
        this.renderer.appendChild(areaDiv, removeButton);
      }

      if (!this.read_only) {
        // Adjunta el detector de doble clic al área misma para eliminar
        this.renderer.listen(areaDiv, "dblclick", () => {
          this.removeArea(area.id, area);
        });
      }

      // Añade el div completo del área de firma al elemento de página actual
      this.renderer.appendChild(pageElement, areaDiv);
    });
  }

  /**
   * Activa una re-renderización de todas las áreas de firma en todas las páginas PDF cargadas actualmente.
   * Esto se llama después de añadir, eliminar o cargar áreas de firma.
   */
  private renderAllSignatureAreas() {
    // Obtiene todos los elementos de página actualmente en el DOM
    const pages = this.el.nativeElement.querySelectorAll(".page");
    pages.forEach((page: HTMLElement) => {
      const pageNumber = parseInt(
        page.getAttribute("data-page-number") || "0",
        10,
      );
      this.renderAreasForPage(page, pageNumber);
    });
  }

  convertToUanatacaFormat(
    x: number,
    y: number,
    width: number,
    height: number,
    renderedWidth: number,
    renderedHeight: number,
  ): string {
    // 1. Escalas basadas en tus valores de consola (1056 x 816)
    const scaleX = this.pdfRealWidth / renderedWidth;
    const scaleY = this.pdfRealHeight / renderedHeight;

    // 2. Punto inicial (Esquina inferior izquierda del recuadro)
    const x1 = Math.round(x * scaleX);
    const y1 = Math.round((renderedHeight - (y + height)) * scaleY);

    // 3. Punto final (Esquina superior derecha del recuadro)
    // Aquí es donde sumamos el ancho/alto para que el valor sea superior, como tú dices
    const x2 = Math.round(x1 + width * scaleX);
    const y2 = Math.round(y1 + height * scaleY);

    // Retornamos los dos puntos: (x1, y1) al (x2, y2)

    return `${x1},${y1},${x2},${y2}`;
  }

  separateValues(data: string): number[] {
    const separatedValues = data.split(",").map((value) => parseInt(value, 10));
    return separatedValues;
  }

  private checkDeviceType() {
    const mobile = this.isMobile();
    const tablet = this.isTablet();

    this.isDiviceType = mobile || tablet;
  }

  /** MENU FLOTANTE */

  /**
   * Alterna la visibilidad del menú flotante.
   */

  /* ========================================= */
  /* Detecta scroll activo */
  /* ========================================= */
  onScroll(): void {
    this.isScrolling = true;

    clearTimeout(this.scrollTimeout);

    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);
  }

  /* ========================================= */
  /* Cambia entre mover y editar */
  /* ========================================= */
  toggleMode(event?: PointerEvent): void {
    // Evita activaciones fantasmas mientras hace scroll
    if (this.isScrolling) {
      return;
    }

    // Evita propagación accidental
    event?.stopPropagation();

    // Cambia estado
    this.isEditMode = !this.isEditMode;

    // =====================================
    // MODO MOVER
    // =====================================

    if (this.isEditMode) {
      const overlays = document.querySelectorAll(".page-overlay");

      overlays.forEach((element: Element) => {
        element.classList.remove("page-overlay");

        element.classList.add("page-overlay2");
      });

      this.page_overlay = "page-overlay2";
    }

    // =====================================
    // MODO EDITAR
    // =====================================
    else {
      const overlays = document.querySelectorAll(".page-overlay2");

      overlays.forEach((element: Element) => {
        element.classList.remove("page-overlay2");

        element.classList.add("page-overlay");
      });

      this.page_overlay = "page-overlay";
    }
  }

  /** MODALLL */

  openModal(content: any) {
    this.modalService
      .open(content, { ariaLabelledBy: "modal-basic-title", size: "lg" })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        },
      );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return "by pressing ESC";
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return "by clicking on a backdrop";
    } else {
      return `with: ${reason}`;
    }
  }

  private genId(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }

    if (
      typeof crypto !== "undefined" &&
      typeof crypto.getRandomValues === "function"
    ) {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);

      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;

      const toHex = (n: number) => n.toString(16).padStart(2, "0");

      const b = Array.from(buf, toHex).join("");

      return `${b.slice(0, 8)}-${b.slice(8, 12)}-${b.slice(12, 16)}-${b.slice(16, 20)}-${b.slice(20)}`;
    }

    throw new Error("Secure UUID generation is not supported in this browser.");
  }

  isTablet(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    const isIpad =
      /ipad|macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

    const isAndroidTablet =
      /android/.test(userAgent) && !/mobile/.test(userAgent);

    return isIpad || isAndroidTablet;
  }

  isMobile(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    return /iphone|ipod|android.*mobile|windows phone/i.test(userAgent);
  }

  isDesktop(): boolean {
    const hasMouse = window.matchMedia("(pointer:fine)").matches;

    return hasMouse && !this.isMobile() && !this.isTablet();
  }

  toastAlert() {
    this.toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });
  }

  alert(icontext: SweetAlertIcon, title: string, text: string = "") {
    this.toast.fire({
      icon: icontext,
      title: title,
      text: text,
    });
  }

  readOnly() {
    if (this.read_only || this.usa_plantilla) {
      const overlays = document.querySelectorAll(".page-overlay");
      overlays.forEach((element) => {
        element.classList.remove("page-overlay");
        element.classList.add("page-overlay2");
      });
      this.page_overlay = "page-overlay2";
    }
  }

  /*AGREGAR FIRMA SIMPLE*/

  agregarFirmaSimple() {
    this.firmante(this.certificado[0]);

    if (this.existFirmante) {
      this.confirmSigner();
    }
  }

  zoomIn() {
    if (this.zoom >= 3) return;

    this.zoom += 0.25;
    setTimeout(() => {
      this.renderAllSignatureAreas();
    }, 100);
  }

  zoomOut() {
    if (this.zoom <= 1) return;

    this.zoom -= 0.25;

    setTimeout(() => {
      this.renderAllSignatureAreas();
    }, 100);
  }

  resetZoom() {
    this.zoom = 1;
    setTimeout(() => {
      this.renderAllSignatureAreas();
    }, 100);
  }
}
